import { useRef } from 'react';
import { useLang } from 'hooks/lang';

import { actions, AddCountryRequest, FullProject, getServiceName } from 'entities';

import { Button, LabeledTextarea, Column, Row, LabeledInput, Tag, Card } from 'components/Blocks';
import { ApproveRejectButtons } from 'components/Buttons';
import { VariableCostInput } from 'components/Inputs';
import { ChatIcon, CheckIcon, ScalingIcon } from 'components/Icons';

import { demographicsRenderer } from 'pages/Projects/components';

import { useToast } from 'providers/Toast';
import { useProjectProfile } from '../../context';
import { useToggle } from 'hooks';

import { MESSAGES } from '../../utils';
import { omitUndefined } from 'utils/object';
import { getCountryLabel } from 'utils/countries';

export function ApproveCountryForm() {
  const {
    project,
    projectModalConfig: { closeModal },
    updateProject,
  } = useProjectProfile();

  const { t } = useLang();

  const { isOn: approved, toggle } = useToggle();

  const { addCountryRequest, tasks } = project;

  const {
    costs,
    demographics,
    reviewComment,
    country,
    project: projectId,
  } = addCountryRequest as AddCountryRequest;

  const commentRef = useRef<HTMLTextAreaElement>(null);

  const { addToast } = useToast();

  const finishRequest = actions.projects.finishProjectCountryAddition.use({
    onSuccess: (response) => {
      updateProject(
        (old) =>
          omitUndefined({
            ...old,

            ...response,

            addCountryRequest: undefined,
          }) as FullProject,
      );

      closeModal();
    },
  });

  const handleAddCountryRequest = async (): Promise<void> => {
    const comment = commentRef.current?.value.trim() || '';

    if (!approved && !comment) return addToast(MESSAGES.addCountryCommentOnReject(t));

    await finishRequest.execute({
      project: projectId,
      comment,
      approved,
    });
  };

  return (
    <>
      <Column gap="6">
        <Row justifyContent="space-between">
          <Tag
            name={getCountryLabel(country)}
            color="primary"
            size="large"
            className="w-fit"
            round={false}
          />

          <ApproveRejectButtons value={approved} onChange={toggle} />
        </Row>

        {!!costs && (
          <Column gap="2">
            {Object.entries(costs).map(([service, costs]) => (
              <Card flow="column" key={`cost--${service}`}>
                <span className="font-semibold text-primary">{getServiceName(service, tasks)}</span>

                <div className="grid grid-cols-3 gap-4 phone:grid-cols-1">
                  {costs.map((cost) => (
                    <VariableCostInput
                      key={`cost-${service}-${cost.name}`}
                      label={cost.name}
                      currency={cost.currency}
                      value={cost.value}
                      disabled
                      type={cost.type}
                    />
                  ))}
                </div>
              </Card>
            ))}
          </Column>
        )}

        {!!demographics && (
          <Column>
            <span className="font-medium text-primary">{t('demographics')}</span>

            <div className="grid grid-cols-2 gap-4 phone:grid-cols-1">
              {demographicsRenderer.map(({ getLabel, key, Icon }) => (
                <LabeledInput
                  key={`demographics-${key}`}
                  width="50%"
                  labelIcon={Icon}
                  label={getLabel(t)}
                  value={project.demographics ? (demographics[key] as string) : ''}
                  disabled
                />
              ))}
            </div>

            <LabeledTextarea
              value={demographics.quotaDetails || 'N/A'}
              label={t('quotas')}
              labelIcon={ScalingIcon}
              disabled
            />
          </Column>
        )}

        {!!reviewComment && (
          <LabeledTextarea
            value={reviewComment}
            labelIcon={ChatIcon}
            label={t('reviewer-comment')}
            disabled
          />
        )}

        <LabeledTextarea ref={commentRef} label={t('add-comments')} />
      </Column>

      <Button onClick={handleAddCountryRequest} loading={finishRequest.loading}>
        <CheckIcon />
        {t('execute-changes')}
      </Button>
    </>
  );
}
